import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Pagination from "../ui/Pagination";
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useMarkNotificationsRead,
  useNotifications,
  useUnreadNotifications,
} from "../../api/queries/notificationQueries";
import usePermissions from "../../hooks/usePermissions";
import { resolveNotificationLink } from "../../utils/notificationLinks";
import { formatDateTime, fromNow } from "../../utils/dateTime";
import { TABS, emptyText, iconFor, listParams } from "./notificationFormat";
import "./NotificationCenter.css";

const EMPTY = [];
const PAGE_SIZE = 20;

/**
 * The large notifications view (modal and /notifications pages, both views):
 * tabs (all / unread / per group), search, date range, select + mark read,
 * full messages, 20 per page. onOpened() runs after "Open" navigates (the
 * modal closes itself there).
 */
const NotificationCenter = ({ onOpened }) => {
  const navigate = useNavigate();
  const { hasHrAccess, employeeId } = usePermissions();
  const [tab, setTab] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(() => new Set());

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchText.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchText]);
  useEffect(() => setSelected(new Set()), [tab, search, from, to, page]);

  const query = useNotifications(
    listParams({ tab, search, from, to, page, limit: PAGE_SIZE }),
  );
  const unread =
    useUnreadNotifications().data?.unread ?? query.data?.unread ?? 0;
  const markOne = useMarkNotificationRead();
  const markMany = useMarkNotificationsRead();
  const markAll = useMarkAllNotificationsRead();
  const rows = query.data?.data || EMPTY;
  const unreadOnPage = rows.filter((n) => !n.readAt);
  const allChecked = rows.length > 0 && rows.every((n) => selected.has(n.id));
  const filtered = !!(search || from || to);

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const open = (n) => {
    if (!n.readAt) markOne.mutate(n.id);
    const target = resolveNotificationLink(n.link, { hasHrAccess, employeeId });
    if (target) {
      navigate(target);
      onOpened?.();
    }
  };
  const markSelected = () =>
    markMany.mutate([...selected], { onSuccess: () => setSelected(new Set()) });

  return (
    <div className="nc">
      <div className="approval-subtabs nc-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={`approval-subtab ${tab === t.key ? "active" : ""}`}
            onClick={() => {
              setTab(t.key);
              setPage(1);
            }}
          >
            {t.label}
            {t.key === "unread" && unread > 0 && (
              <span className="nc-count">{unread > 99 ? "99+" : unread}</span>
            )}
          </button>
        ))}
      </div>

      <div className="nc-toolbar">
        <div className="position-relative nc-search">
          <i className="bi bi-search position-absolute top-50 translate-middle-y ms-3 text-slate-500 fs-080"></i>
          <input
            type="search"
            className="filter-input w-100 ps-5"
            placeholder="Search title or message"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            aria-label="Search notifications"
          />
        </div>
        <div className="d-flex align-items-center gap-1">
          <input
            type="date"
            className="filter-input"
            value={from}
            max={to || undefined}
            onChange={(e) => {
              setFrom(e.target.value);
              setPage(1);
            }}
            aria-label="From"
          />
          <span className="text-slate-400">–</span>
          <input
            type="date"
            className="filter-input"
            value={to}
            min={from || undefined}
            onChange={(e) => {
              setTo(e.target.value);
              setPage(1);
            }}
            aria-label="To"
          />
        </div>
        <div className="d-flex gap-2 ms-auto">
          {selected.size > 0 && (
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={markSelected}
              disabled={markMany.isPending}
            >
              Mark {selected.size} as read
            </button>
          )}
          <button
            type="button"
            className="btn btn-sm btn-light border"
            onClick={() => markAll.mutate()}
            disabled={markAll.isPending || unread === 0}
          >
            <i className="bi bi-check2-all me-1"></i>Mark all as read
          </button>
        </div>
      </div>

      {query.isError ? (
        <div className="nc-empty text-danger">
          <i className="bi bi-exclamation-triangle"></i>Could not load
          notifications.
        </div>
      ) : query.isLoading ? (
        <div className="text-center py-5">
          <span
            className="spinner-border text-primary"
            role="status"
            aria-label="Loading"
          ></span>
        </div>
      ) : rows.length === 0 ? (
        <div className="nc-empty">
          <i className="bi bi-bell-slash"></i>
          {emptyText(tab, filtered)}
        </div>
      ) : (
        <>
          <div className="nc-select-all">
            <input
              id="nc-all"
              type="checkbox"
              className="form-check-input"
              checked={allChecked}
              onChange={() =>
                setSelected(
                  allChecked ? new Set() : new Set(rows.map((n) => n.id)),
                )
              }
            />
            <label htmlFor="nc-all" className="fs-075 text-slate-500">
              Select this page
              {unreadOnPage.length ? ` (${unreadOnPage.length} unread)` : ""}
            </label>
          </div>
          <ul className="nc-list">
            {rows.map((n) => {
              const [icon, tone] = iconFor(n);
              const isUnread = !n.readAt;
              return (
                <li
                  key={n.id}
                  className={`nc-row ${isUnread ? "is-unread" : ""} ${selected.has(n.id) ? "is-selected" : ""}`}
                >
                  <input
                    type="checkbox"
                    className="form-check-input mt-1"
                    checked={selected.has(n.id)}
                    onChange={() => toggle(n.id)}
                    aria-label={`Select ${n.title}`}
                  />
                  <span
                    className={`notif-icon notif-icon--${tone}`}
                    aria-hidden="true"
                  >
                    <i className={`bi ${icon}`}></i>
                  </span>
                  <div className="nc-body">
                    <div className="nc-title">{n.title}</div>
                    {n.message && <p className="nc-message">{n.message}</p>}
                    <div className="nc-meta">
                      <span title={formatDateTime(n.createdAt)}>
                        {fromNow(n.createdAt)}
                      </span>
                      {n.group && <span>· {n.group}</span>}
                    </div>
                  </div>
                  <div className="nc-actions">
                    {n.link && (
                      <button
                        type="button"
                        className="btn btn-sm btn-light border"
                        onClick={() => open(n)}
                      >
                        Open
                      </button>
                    )}
                    <button
                      type="button"
                      className="nc-dot-btn"
                      onClick={() => isUnread && markOne.mutate(n.id)}
                      disabled={!isUnread}
                      title={isUnread ? "Mark as read" : "Read"}
                      aria-label={isUnread ? `Mark ${n.title} as read` : "Read"}
                    >
                      <span
                        className={isUnread ? "notif-dot" : "nc-dot-read"}
                      ></span>
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
          <Pagination
            page={page}
            limit={PAGE_SIZE}
            total={query.data?.total || 0}
            onPageChange={setPage}
            onLimitChange={() => {}}
            pageSizes={[PAGE_SIZE]}
          />
        </>
      )}
    </div>
  );
};

export default NotificationCenter;
